import { Card } from '@/components/ui/Card'
import { Tooltip } from '@/components/ui/Tooltip'
import { Clock, Users, Building2, TrendingUp, Info } from 'lucide-react'
import { usePresalesDashboardStore } from '../store'
import { TIME_PERIODS } from './TimePeriodSelector'
import type { HoursAnalyticsResult } from '@/api/types'

const MONTH_LABELS: Record<string, string> = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
}

const PERSON_COLORS = ['bg-violet-500', 'bg-sky-500', 'bg-emerald-500', 'bg-amber-500']
const PERSON_TEXT_COLORS = ['text-violet-400', 'text-sky-400', 'text-emerald-400', 'text-amber-400']

const CATEGORY_COLORS: Record<string, string> = {
  'Demo': 'bg-blue-500',
  'Discovery': 'bg-violet-500',
  'Technical': 'bg-emerald-500',
  'Prep': 'bg-amber-500',
  'Other': 'bg-gray-500',
}
const CATEGORY_TEXT_COLORS: Record<string, string> = {
  'Demo': 'text-blue-400',
  'Discovery': 'text-violet-400',
  'Technical': 'text-emerald-400',
  'Prep': 'text-amber-400',
  'Other': 'text-gray-400',
}

function formatMonth(month: string): string {
  const parts = month.split('-')
  return MONTH_LABELS[parts[1]] || parts[1]
}

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

function shortName(full: string): string {
  return full.split(' ')[0]
}

function getPeriodLabel(timePeriod: string): string {
  const match = TIME_PERIODS.find(p => p.value === timePeriod)
  return match?.label || timePeriod
}

export function HoursAnalyticsSection() {
  const { hoursAnalytics, isLoadingHoursAnalytics, timePeriod } = usePresalesDashboardStore()
  const periodLabel = getPeriodLabel(timePeriod)

  if (isLoadingHoursAnalytics || !hoursAnalytics) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Hours Reporting
          </h2>
          <span className="text-xs text-[var(--text-muted)] font-normal normal-case">
            — {periodLabel}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="h-20 bg-[var(--bg-secondary)] animate-pulse rounded" />
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          Hours Reporting
        </h2>
        <span className="text-xs text-[var(--text-muted)] font-normal normal-case">
          — {periodLabel}
        </span>
      </div>

      <div className="space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <SummaryCard
            icon={<Clock className="h-4 w-4 text-blue-400" />}
            label="Total Hours"
            sublabel={periodLabel}
            value={`${hoursAnalytics.total_hours}h`}
          />
          <SummaryCard
            icon={<Users className="h-4 w-4 text-violet-400" />}
            label="Meetings"
            sublabel={periodLabel}
            value={hoursAnalytics.total_meetings}
          />
          <SummaryCard
            icon={<Building2 className="h-4 w-4 text-emerald-400" />}
            label="Active Deals"
            sublabel={periodLabel}
            value={hoursAnalytics.total_deals}
          />
        </div>

        {/* Monthly hours by person with category breakdown */}
        <MonthlyHoursChart
          data={hoursAnalytics.monthly_by_person}
          categoryData={hoursAnalytics.monthly_by_category}
        />

        {/* Row: Top customers + Hours to value */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopCustomersCard data={hoursAnalytics.top_customers} periodLabel={periodLabel} />
          <HoursToValueCard data={hoursAnalytics.hours_to_value} periodLabel={periodLabel} />
        </div>
      </div>
    </div>
  )
}

function SummaryCard({ icon, label, sublabel, value }: { icon: React.ReactNode; label: string; sublabel?: string; value: string | number }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-[var(--text-muted)]">{label}</span>
      </div>
      <div className="text-2xl font-bold text-[var(--text-primary)]">{value}</div>
      {sublabel && <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{sublabel}</div>}
    </Card>
  )
}

function MonthlyHoursChart({ data, categoryData }: {
  data: HoursAnalyticsResult['monthly_by_person']
  categoryData: HoursAnalyticsResult['monthly_by_category']
}) {
  if (data.length === 0) {
    return (
      <Card className="p-4">
        <div className="text-sm text-[var(--text-muted)] text-center py-4">No hours data available</div>
      </Card>
    )
  }

  // Group by month
  const months = Array.from(new Set(data.map(d => d.month))).sort()
  const people = Array.from(new Set(data.map(d => d.person))).sort()
  const maxHours = Math.max(...data.map(d => d.hours), 1)

  // Get categories for breakdown
  const categories = Array.from(new Set(categoryData.map(d => d.category))).sort()

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-400" />
          <span className="text-sm font-medium text-[var(--text-primary)]">Monthly Hours on Customer Work</span>
          <Tooltip content={
            <div className="max-w-xs text-xs space-y-1">
              <div>Hours spent on tagged customer meetings — demos, discovery calls, technical deep-dives, and prep work.</div>
              <div className="text-[var(--text-muted)]">Only includes events that have been tagged and linked to a deal or prospect in the calendar tagger.</div>
            </div>
          }>
            <Info className="h-3 w-3 text-[var(--text-muted)] cursor-help" />
          </Tooltip>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[var(--text-muted)]">
          {people.map((person, idx) => (
            <div key={person} className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-sm ${PERSON_COLORS[idx % PERSON_COLORS.length]}`} />
              <span>{shortName(person)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {months.map((month) => {
          const monthData = data.filter(d => d.month === month)
          const monthTotal = monthData.reduce((s, d) => s + d.hours, 0)
          const monthCatData = categoryData.filter(d => d.month === month)

          return (
            <div key={month}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-[var(--text-muted)]">{formatMonth(month)}</span>
                <span className="text-[10px] text-[var(--text-muted)]">{monthTotal.toFixed(1)}h total</span>
              </div>
              {/* Person bars */}
              <div className="space-y-1">
                {people.map((person, idx) => {
                  const entry = monthData.find(d => d.person === person)
                  const hours = entry?.hours || 0
                  const pct = (hours / maxHours) * 100

                  return (
                    <div key={person} className="flex items-center gap-2">
                      <span className={`text-[10px] w-12 shrink-0 truncate ${PERSON_TEXT_COLORS[idx % PERSON_TEXT_COLORS.length]}`}>
                        {shortName(person)}
                      </span>
                      <div className="flex-1 h-4 bg-[var(--bg-tertiary)] rounded overflow-hidden">
                        <div
                          className={`h-full ${PERSON_COLORS[idx % PERSON_COLORS.length]} rounded transition-all duration-500`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                      <span className={`text-[11px] font-semibold w-10 text-right shrink-0 ${PERSON_TEXT_COLORS[idx % PERSON_TEXT_COLORS.length]}`}>
                        {hours.toFixed(1)}h
                      </span>
                    </div>
                  )
                })}
              </div>
              {/* Category breakdown for the month */}
              {monthCatData.length > 0 && (
                <div className="mt-1.5 ml-14 flex flex-wrap gap-x-3 gap-y-0.5">
                  {categories.map((cat) => {
                    const catHours = monthCatData
                      .filter(d => d.category === cat)
                      .reduce((s, d) => s + d.hours, 0)
                    if (catHours === 0) return null
                    return (
                      <div key={cat} className="flex items-center gap-1">
                        <div className={`w-1.5 h-1.5 rounded-full ${CATEGORY_COLORS[cat] || CATEGORY_COLORS['Other']}`} />
                        <span className={`text-[9px] ${CATEGORY_TEXT_COLORS[cat] || CATEGORY_TEXT_COLORS['Other']}`}>
                          {cat} {catHours.toFixed(1)}h
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Category legend */}
      {categories.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--border-color)] flex flex-wrap gap-3 text-[10px] text-[var(--text-muted)]">
          {categories.map((cat) => (
            <div key={cat} className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${CATEGORY_COLORS[cat] || CATEGORY_COLORS['Other']}`} />
              <span>{cat}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function TopCustomersCard({ data, periodLabel }: { data: HoursAnalyticsResult['top_customers']; periodLabel: string }) {
  const maxHours = data.length > 0 ? Math.max(...data.map(d => d.hours)) : 1

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="h-4 w-4 text-emerald-400" />
        <span className="text-sm font-medium text-[var(--text-primary)]">Most Intensive Customers</span>
        <Tooltip content={
          <div className="max-w-xs text-xs space-y-1">
            <div>Ranked by total presales hours spent on tagged meetings for each customer.</div>
            <div className="text-[var(--text-muted)]">Period: {periodLabel}</div>
          </div>
        }>
          <Info className="h-3 w-3 text-[var(--text-muted)] cursor-help" />
        </Tooltip>
      </div>

      {data.length === 0 ? (
        <div className="text-sm text-[var(--text-muted)] text-center py-4">No data</div>
      ) : (
        <div className="space-y-2">
          {data.slice(0, 10).map((customer, idx) => (
            <div key={customer.company} className="flex items-center gap-2">
              <span className="text-[10px] text-[var(--text-muted)] w-4 shrink-0">{idx + 1}.</span>
              <span className="text-xs text-[var(--text-primary)] w-32 shrink-0 truncate" title={customer.company}>
                {customer.company}
              </span>
              <div className="flex-1 h-3 bg-[var(--bg-tertiary)] rounded overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded transition-all duration-500"
                  style={{ width: `${(customer.hours / maxHours) * 100}%` }}
                />
              </div>
              <span className="text-[11px] font-semibold text-emerald-400 w-10 text-right shrink-0">
                {customer.hours}h
              </span>
              <span className="text-[10px] text-[var(--text-muted)] w-8 text-right shrink-0">
                {customer.meetings}mtg
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function HoursToValueCard({ data, periodLabel }: { data: HoursAnalyticsResult['hours_to_value']; periodLabel: string }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-amber-400" />
        <span className="text-sm font-medium text-[var(--text-primary)]">Hours-to-Value Ratio</span>
        <Tooltip content={
          <div className="max-w-xs text-xs space-y-2">
            <div className="font-semibold">How $/Hour is calculated:</div>
            <div>$/Hour = Total deal value in bucket ÷ Total presales hours spent on those deals</div>
            <div className="text-[var(--text-muted)]">
              <div>• Deal value comes from the HubSpot deal amount (AUD)</div>
              <div>• Hours come from tagged calendar events linked to those deals</div>
              <div>• Deals with $0 value are excluded</div>
            </div>
            <div className="pt-1 border-t border-gray-700 text-[var(--text-muted)]">
              <div>Color coding: <span className="text-green-400">Green</span> = &gt;$5K/hr, <span className="text-amber-400">Amber</span> = $1-5K/hr, <span className="text-red-400">Red</span> = &lt;$1K/hr</div>
            </div>
            <div className="text-[var(--text-muted)]">Period: {periodLabel}</div>
          </div>
        }>
          <Info className="h-3 w-3 text-[var(--text-muted)] cursor-help" />
        </Tooltip>
      </div>

      {data.length === 0 ? (
        <div className="text-sm text-[var(--text-muted)] text-center py-4">No data</div>
      ) : (
        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-4 gap-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] pb-1 border-b border-[var(--border-color)]">
            <span>Deal Size</span>
            <span className="text-right">Deals</span>
            <span className="text-right">Hours</span>
            <span className="text-right">$/Hour</span>
          </div>

          {data.map((bucket) => {
            const isHighValue = bucket.value_per_hour > 5000
            const isLowValue = bucket.value_per_hour < 1000 && bucket.value_per_hour > 0

            return (
              <div key={bucket.bucket} className="grid grid-cols-4 gap-2 items-center text-xs">
                <span className="text-[var(--text-primary)] font-medium">{bucket.bucket}</span>
                <span className="text-right text-[var(--text-muted)]">{bucket.deal_count}</span>
                <span className="text-right text-[var(--text-muted)]">{bucket.total_hours}h</span>
                <span className={`text-right font-semibold ${isHighValue ? 'text-green-400' : isLowValue ? 'text-red-400' : 'text-amber-400'}`}>
                  {bucket.value_per_hour ? formatCurrency(bucket.value_per_hour) : '--'}
                </span>
              </div>
            )
          })}

          {/* Total row */}
          <div className="grid grid-cols-4 gap-2 items-center text-xs pt-1 border-t border-[var(--border-color)]">
            <span className="text-[var(--text-primary)] font-semibold">Total</span>
            <span className="text-right font-semibold text-[var(--text-primary)]">
              {data.reduce((s, d) => s + d.deal_count, 0)}
            </span>
            <span className="text-right font-semibold text-[var(--text-primary)]">
              {data.reduce((s, d) => s + d.total_hours, 0).toFixed(1)}h
            </span>
            <span className="text-right font-semibold text-amber-400">
              {(() => {
                const totalHrs = data.reduce((s, d) => s + d.total_hours, 0)
                const totalVal = data.reduce((s, d) => s + d.total_value, 0)
                return totalHrs > 0 ? formatCurrency(totalVal / totalHrs) : '--'
              })()}
            </span>
          </div>
        </div>
      )}
    </Card>
  )
}
